
import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export const useCallDurationManager = () => {
  const [isCallActive, setIsCallActive] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const { toast } = useToast();

  const getBookingDuration = async (mentorId: string, menteeId: string, bookingDate: string) => {
    try {
      // First try to get duration from session_bookings
      const { data: booking, error: bookingError } = await supabase
        .from('session_bookings')
        .select('start_time, end_time')
        .eq('mentor_id', mentorId)
        .eq('mentee_id', menteeId)
        .eq('booked_date', bookingDate)
        .single();

      if (!bookingError && booking) {
        const startTime = new Date(`1970-01-01T${booking.start_time}`);
        const endTime = new Date(`1970-01-01T${booking.end_time}`);
        const durationMs = endTime.getTime() - startTime.getTime();
        const durationMinutes = Math.floor(durationMs / (1000 * 60));
        console.log(`Found booking duration: ${durationMinutes} minutes`);
        return durationMinutes;
      }

      // Fallback: try to get from booking preferences (default session duration)
      const { data: preferences, error: prefError } = await supabase
        .from('booking_preferences')
        .select('session_duration')
        .eq('mentor_id', mentorId)
        .single();

      if (!prefError && preferences) {
        console.log(`Using default session duration: ${preferences.session_duration} minutes`);
        return preferences.session_duration;
      }

      // Default fallback
      console.log('Using default duration: 60 minutes');
      return 60;
    } catch (error) {
      console.error('Error getting booking duration:', error);
      return 60; // Default 1 hour
    }
  };

  const startCallTimer = useCallback(async (mentorId: string, menteeId: string, bookingDate?: string) => {
    try {
      const duration = await getBookingDuration(mentorId, menteeId, bookingDate || new Date().toISOString().split('T')[0]);
      setCallDuration(duration);
      setIsCallActive(true);
      
      toast({
        title: "Call Started",
        description: `Call duration: ${duration} minutes`,
      });
    } catch (error) {
      console.error('Error starting call timer:', error);
      toast({
        title: "Warning",
        description: "Could not determine call duration. Please monitor time manually.",
        variant: "destructive",
      });
    }
  }, [toast]);

  const endCallTimer = useCallback(() => {
    setIsCallActive(false);
    setCallDuration(0);
    
    toast({
      title: "Call Ended",
      description: "The scheduled call time has ended.",
    });
  }, [toast]);

  return {
    isCallActive,
    callDuration,
    startCallTimer,
    endCallTimer
  };
};
